import { cn, formatRatio } from '../../lib/utils';
import { severityPillStyle } from '../../lib/map-utils';

interface RankingsSeverityPillProps {
  /** Numeric ratio rendered inside the pill (formatted to 2 decimals). */
  readonly ratio: number;
}

/**
 * Rankings-table severity pill — 2026-05-18 feedback round.
 *
 * The pill now carries a **continuous gradient** anchored at the
 * extremes seen in the data: San Diego-Chula Vista (0.22) renders as
 * the darkest red; Boston-Cambridge-Newton (0.87) renders as the
 * lightest yellow. Above 1.00 the gradient continues toward green to
 * match the Map page's color encoding. Categorical status colors
 * (the prior rose/gold/green palette) are gone — same reasoning as
 * the Map: the report doesn't argue for a "balanced" band.
 *
 * Text color flips to white on the dark-red and dark-green endpoints
 * so the ratio reads at WCAG AA contrast.
 *
 * Width + typography unchanged so existing column alignment holds.
 */
export function RankingsSeverityPill({ ratio }: RankingsSeverityPillProps) {
  const { bg, text } = severityPillStyle(ratio);
  return (
    <span
      aria-label={`Credentials-to-jobs ratio: ${formatRatio(ratio)}`}
      style={{ background: bg, color: text }}
      className={cn(
        'inline-flex min-w-[52px] items-center justify-center rounded-pill px-3 py-1',
        'text-[12px] font-bold leading-tight tabular-nums',
      )}
    >
      {formatRatio(ratio)}
    </span>
  );
}
